import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import CodeEditor from "../../../components/coding/CodeEditor";

// /admin/coding-questions/:questionId — attach/edit coding config for an
// existing question from the question bank.
//
// Loads:
//   GET  /job-posting/coding-questions/{id}           question + testCases (or 404)
// Saves:
//   POST /job-posting/coding-questions                first save (creates row)
//   PUT  /job-posting/coding-questions/{id}           subsequent saves
//   POST /job-posting/coding-questions/{id}/test-cases/bulk  replace test cases
//
// The parent question text/title lives in amplehire_interview_questions_t
// and is managed by the existing question-bank UI. This page only manages
// coding-specific config.

const LANGUAGES = ["javascript", "python", "java", "cpp", "go", "ruby", "csharp"];
const DIFFICULTIES = ["EASY", "MEDIUM", "HARD", "EXPERT"];

const EMPTY_TEST = (order = 1) => ({
  id: null,
  input: "",
  expectedOutput: "",
  isHidden: false,
  weight: 1.0,
  displayOrder: order,
  description: "",
});

export default function CodingQuestionEditor() {
  const { questionId } = useParams();
  const nav = useNavigate();

  const [language, setLanguage] = useState("python");
  const [starterCode, setStarterCode] = useState("");
  const [solutionCode, setSolutionCode] = useState("");
  const [timeLimitMs, setTimeLimitMs] = useState(5000);
  const [memoryLimitKb, setMemoryLimitKb] = useState(256000);
  const [difficulty, setDifficulty] = useState("MEDIUM");
  const [allowedLangs, setAllowedLangs] = useState([]);
  const [testCases, setTestCases] = useState([EMPTY_TEST(1)]);
  const [existingConfig, setExistingConfig] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);

  const flashToast = (msg, ms = 2200) => { setToast(msg); setTimeout(() => setToast(null), ms); };

  useEffect(() => {
    document.title = `Coding question ${questionId} - Ample Hire`;
    let cancelled = false;
    setLoading(true);
    axiosInstance
      .get(`${baseUrl}/job-posting/coding-questions/${questionId}`)
      .then((r) => {
        if (cancelled) return;
        const q = r.data?.question;
        if (q) {
          setExistingConfig(true);
          setLanguage(q.language || "python");
          setStarterCode(q.starterCode || "");
          setSolutionCode(q.solutionCode || "");
          setTimeLimitMs(q.timeLimitMs || 5000);
          setMemoryLimitKb(q.memoryLimitKb || 256000);
          setDifficulty(q.difficulty || "MEDIUM");
          try { setAllowedLangs(JSON.parse(q.allowedLanguages || "[]")); }
          catch { setAllowedLangs([]); }
        }
        const tcs = Array.isArray(r.data?.testCases) ? r.data.testCases : [];
        if (tcs.length > 0) {
          setTestCases(tcs.map((tc, i) => ({
            id: tc.id,
            input: tc.input || "",
            expectedOutput: tc.expectedOutput || "",
            isHidden: !!tc.isHidden,
            weight: tc.weight != null ? Number(tc.weight) : 1.0,
            displayOrder: tc.displayOrder ?? i + 1,
            description: tc.description || "",
          })));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          // New question — keep empty form
          setExistingConfig(false);
        } else {
          setError(e?.message || "Couldn't load coding question");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [questionId]);

  const toggleAllowedLang = (lang) => {
    setAllowedLangs((prev) =>
      prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang]
    );
  };

  const updateTest = (idx, patch) => {
    setTestCases((prev) => prev.map((tc, i) => (i === idx ? { ...tc, ...patch } : tc)));
  };

  const addTest = () => {
    setTestCases((prev) => [...prev, EMPTY_TEST(prev.length + 1)]);
  };

  const removeTest = (idx) => {
    setTestCases((prev) => prev.filter((_, i) => i !== idx).map((tc, i) => ({ ...tc, displayOrder: i + 1 })));
  };

  // Validation
  const validation = (() => {
    if (!starterCode.trim()) return "Starter code is required";
    if (testCases.length === 0) return "Add at least one test case";
    for (let i = 0; i < testCases.length; i++) {
      const tc = testCases[i];
      if (!tc.input && tc.input !== "") return `Test ${i + 1} input is required`;
      if (!tc.expectedOutput && tc.expectedOutput !== "") return `Test ${i + 1} expected output is required`;
    }
    return null;
  })();

  const weightSum = testCases.reduce((s, tc) => s + Number(tc.weight || 0), 0);
  const weightWarn = Math.abs(weightSum - 1.0) > 0.001 && Math.abs(weightSum - testCases.length) > 0.001;

  const save = async () => {
    if (validation) { setError(validation); return; }
    setSaving(true);
    setError(null);
    try {
      const body = {
        questionId: Number(questionId),
        language,
        starterCode,
        solutionCode,
        timeLimitMs: Number(timeLimitMs),
        memoryLimitKb: Number(memoryLimitKb),
        difficulty,
        allowedLanguages: JSON.stringify(allowedLangs),
      };
      if (existingConfig) {
        await axiosInstance.put(`${baseUrl}/job-posting/coding-questions/${questionId}`, body);
      } else {
        await axiosInstance.post(`${baseUrl}/job-posting/coding-questions`, body);
        setExistingConfig(true);
      }
      // Bulk-replace test cases.
      const tcBody = testCases.map((tc) => ({
        input: tc.input,
        expectedOutput: tc.expectedOutput,
        isHidden: tc.isHidden,
        weight: tc.weight,
        displayOrder: tc.displayOrder,
        description: tc.description,
      }));
      await axiosInstance.post(`${baseUrl}/job-posting/coding-questions/${questionId}/test-cases/bulk`, tcBody);
      flashToast("Saved");
    } catch (e) {
      setError(e?.data?.error || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-vf-muted">Loading…</div>;

  return (
    <div className="page-enter max-w-6xl">
      <div className="flex items-start justify-between mb-5">
        <div>
          <button onClick={() => nav("/admin/question-bank")} className="text-xs text-vf-muted hover:underline mb-1">← Back to question bank</button>
          <h1 className="text-2xl font-semibold text-vf-base">
            Coding config · Question #{questionId}
          </h1>
          <p className="text-sm text-vf-muted mt-1">
            {existingConfig ? "Editing existing config" : "Attach coding config to this question"}
          </p>
        </div>
        <div className="flex gap-2 items-start">
          <button
            onClick={() => nav(`/candidate/coding/${questionId}`)}
            className="px-3 py-1.5 rounded border border-slate-300 text-xs"
          >
            Preview candidate view ↗
          </button>
          <button
            onClick={save}
            disabled={saving || !!validation}
            className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs font-medium disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 rounded p-3 text-xs text-red-700 mb-4">{error}</div>
      )}
      {toast && (
        <div className="border border-emerald-200 bg-emerald-50 rounded p-3 text-xs text-emerald-700 mb-4">{toast}</div>
      )}

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7 space-y-4">
          <Section title="Language + difficulty">
            <Row>
              <Field label="Default language">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                >
                  {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Field>
              <Field label="Difficulty">
                <select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm"
                >
                  {DIFFICULTIES.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            </Row>
            <Field label="Allowed languages (candidate can pick from these; empty = all)">
              <div className="flex flex-wrap gap-1.5">
                {LANGUAGES.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => toggleAllowedLang(l)}
                    className={`px-2.5 py-1 rounded-full text-xs border ${
                      allowedLangs.includes(l)
                        ? "bg-vf-primary text-white border-vf-primary"
                        : "bg-white text-vf-base border-slate-300"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </Field>
            <Row>
              <Field label="Time limit (ms)">
                <input type="number" value={timeLimitMs}
                       onChange={(e) => setTimeLimitMs(e.target.value)}
                       className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
              </Field>
              <Field label="Memory limit (KB)">
                <input type="number" value={memoryLimitKb}
                       onChange={(e) => setMemoryLimitKb(e.target.value)}
                       className="w-full px-3 py-2 border border-slate-300 rounded text-sm" />
              </Field>
            </Row>
          </Section>

          <Section title="Starter code (shown to candidate)">
            <div className="border border-slate-300 rounded overflow-hidden">
              <CodeEditor value={starterCode} onChange={(v) => setStarterCode(v ?? "")}
                          language={language} height="280px" />
            </div>
          </Section>

          <Section title="Reference solution (never sent to candidate)">
            <div className="border border-slate-300 rounded overflow-hidden">
              <CodeEditor value={solutionCode} onChange={(v) => setSolutionCode(v ?? "")}
                          language={language} height="220px" />
            </div>
            <div className="text-[11px] text-vf-muted mt-1">
              Stored for grading reference. v1 doesn't auto-check that the solution passes its own test cases — that's plan-doc follow-up.
            </div>
          </Section>
        </div>

        <div className="col-span-12 lg:col-span-5 space-y-4">
          <Section title={`Test cases (${testCases.length})`}>
            <div className="space-y-3">
              {testCases.map((tc, idx) => (
                <div key={idx} className="border border-slate-200 rounded p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="text-xs font-semibold text-vf-base">Test {idx + 1}</div>
                    <label className="text-[11px] text-vf-muted flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={tc.isHidden}
                             onChange={(e) => updateTest(idx, { isHidden: e.target.checked })} />
                      Hidden
                    </label>
                    <div className="ml-auto flex items-center gap-1">
                      <label className="text-[11px] text-vf-muted">Weight</label>
                      <input type="number" step="0.1" min="0" value={tc.weight}
                             onChange={(e) => updateTest(idx, { weight: Number(e.target.value) || 0 })}
                             className="w-16 px-2 py-0.5 border border-slate-300 rounded text-xs" />
                      <button onClick={() => removeTest(idx)}
                              className="text-red-600 hover:bg-red-50 rounded px-1.5 py-0.5 text-xs ml-1">
                        ✕
                      </button>
                    </div>
                  </div>
                  <input type="text" value={tc.description}
                         onChange={(e) => updateTest(idx, { description: e.target.value })}
                         placeholder="Description (optional, e.g. 'Empty array should return 0')"
                         className="w-full px-2 py-1 border border-slate-300 rounded text-xs" />
                  <div>
                    <label className="block text-[11px] text-vf-muted mb-0.5">Input</label>
                    <textarea value={tc.input}
                              onChange={(e) => updateTest(idx, { input: e.target.value })}
                              rows={2}
                              className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-vf-muted mb-0.5">Expected output</label>
                    <textarea value={tc.expectedOutput}
                              onChange={(e) => updateTest(idx, { expectedOutput: e.target.value })}
                              rows={2}
                              className="w-full px-2 py-1 border border-slate-300 rounded text-xs font-mono" />
                  </div>
                </div>
              ))}
            </div>
            <button onClick={addTest}
                    className="w-full mt-3 px-3 py-1.5 rounded border border-dashed border-slate-300 text-xs text-vf-muted hover:bg-slate-50">
              + Add test case
            </button>
            {testCases.length > 0 && (
              <div className={`mt-2 text-[11px] ${weightWarn ? "text-amber-700" : "text-vf-muted"}`}>
                Weight sum: <b>{weightSum.toFixed(2)}</b>
                {weightWarn && " — not 1.0 and not equal to test count; scores will still compute by ratio."}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-vf-muted font-semibold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
}
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-vf-muted mb-1">{label}</label>
      {children}
    </div>
  );
}
